import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import { industryHierarchyOptions } from '@/lib/industry';
import type { FilterOption, FilterSelections, SelectedFilterValue } from '@/components/shared';
import type { TalFile } from '@/stores/audience-filters-store';
import type { TechnographicSelection } from '@/lib/technographics/types';
import { COMPANY_FILTER_CONFIGS, CONTACT_FILTER_CONFIGS } from './filter-configs';
import type { ExploreFilterConfig } from './filter-config-types';
import { technologyFromSelections } from '@/lib/technographics/technology-definition';

/**
 * A saved audience — the filter rail's state, given a name (NPJB-711).
 *
 * The wire shape mirrors the backend's `AudienceDefinition`
 * (`app/application/icp/icp_audience_schemas.py`) key for key. There is no mapping
 * layer on purpose: the eight filter keys here are the same keys the rail is keyed by
 * in `filter-configs.ts`, so a definition can be written straight into `stagedFilters`
 * and read straight back out. What a definition sets, part by part, is `audience-parts.ts`.
 */

/** The eight filter groups the rail can express today. */
export interface SavedAudienceFilters {
  industry: SelectedFilterValue[];
  subIndustry: SelectedFilterValue[];
  employeeRange: SelectedFilterValue[];
  revenueRange: SelectedFilterValue[];
  seniority: SelectedFilterValue[];
  jobFunction: SelectedFilterValue[];
  geoRegion: SelectedFilterValue[];
  country: SelectedFilterValue[];
}

/**
 * Signal selections. `source` holds journey origins (`email`, `dz_publications`,
 * `social`, `web_views`), empty meaning every source; the filters drawer and the audience
 * page's sources chip set it. `stage` is carried and always empty — nothing expresses it.
 */
export interface SavedAudienceSignals {
  topics: string[];
  source: string[];
  stage: string[];
}

/**
 * The technology an account uses, and the technology it does not.
 *
 * The filters drawer's Technographics field fills `uses` (NPJB-764). Kept apart rather than
 * folded into one signed list: "does not use Splunk" is not a kind of "uses Splunk", and NPJB-725 AC7
 * turns on the two staying distinguishable.
 */
export interface SavedAudienceTechnology {
  uses: string[];
  notUses: string[];
}

/**
 * A Target List saved with the audience (NPJB-701 C2). The client sends `uploadId` alone —
 * the id `POST /audience-explorer/tal-uploads` returned, the same one the count stream takes
 * as `talUploadIds`. The server fills `fileName` and `domainsCleaned` from the upload row on
 * every read and ignores any value a client sends.
 */
export interface SavedTargetList {
  uploadId: string;
  fileName?: string;
  domainsCleaned?: number;
}

export interface AudienceDefinition {
  version: number;
  filters: SavedAudienceFilters;
  signals: SavedAudienceSignals;
  technology: SavedAudienceTechnology;
  /** Read back as `[]` for a row saved before NPJB-701. */
  targetLists: SavedTargetList[];
  /** The last applied AI Search prompt (NPJB-836 C1); `null` on a row saved before it. */
  aiSearchPrompt?: string | null;
}

/** The row's size line (NPJB-723 AC3). */
export interface AudienceMetrics {
  /** `null` until onboarding step 6 resolves the ICP — not `0`, which would be a size. */
  accounts: number | null;
  contacts: number | null;
}

/** One row of the switcher, as the API returns it. */
export interface SavedAudience {
  id: string;
  title: string;
  filters: AudienceDefinition;
  creatorName: string;
  /** `active` or `archived`. `isArchived` is the same fact, asked the common way. */
  status: string;
  isArchived: boolean;
  metrics: AudienceMetrics;
  createdAt: string;
  updatedAt: string;
}

/** The rail's filter keys, in the order the panel draws them. */
export const DEFINITION_FILTER_KEYS = [
  'industry',
  'subIndustry',
  'employeeRange',
  'revenueRange',
  'seniority',
  'jobFunction',
  'geoRegion',
  'country',
] as const satisfies readonly (keyof SavedAudienceFilters)[];

const emptyFilters = (): SavedAudienceFilters => ({
  industry: [],
  subIndustry: [],
  employeeRange: [],
  revenueRange: [],
  seniority: [],
  jobFunction: [],
  geoRegion: [],
  country: [],
});

/**
 * Build a definition from what the rail currently holds.
 *
 * Reads **staged**, not applied. The seller saves what they can see checked, and the
 * Save row is not gated on Apply — the same call `use-create-audience-handoff.ts`
 * makes, and for the same reason.
 */
export const buildDefinition = (
  stagedFilters: FilterSelections,
  stagedSignalTopics: string[],
  stagedSignalSources: string[] = [],
  talFiles: TalFile[] = [],
  technographics: readonly TechnographicSelection[] = [],
): AudienceDefinition => {
  const filters = emptyFilters();
  for (const key of DEFINITION_FILTER_KEYS) {
    filters[key] = stagedFilters[key] ?? [];
  }
  // Only lists that finished uploading have an id; ids only — the server fills the rest.
  const uploadIds = new Set(
    talFiles.flatMap((file) => (file.status === 'done' && file.uploadId ? [file.uploadId] : [])),
  );
  return {
    version: 1,
    filters,
    // `stage` stays empty until something can express it — see `SavedAudienceSignals`.
    signals: { topics: stagedSignalTopics, source: stagedSignalSources, stage: [] },
    technology: technologyFromSelections(technographics),
    targetLists: [...uploadIds].map((uploadId) => ({ uploadId })),
    // The save sets it: it depends on the audience being edited (`promptForSave`).
    aiSearchPrompt: null,
  };
};

/**
 * The prompt a save stores (NPJB-836 D3, D8, D14): the one applied in this drawer, `null`
 * once Clear threw it away, else — `applied` untouched (`undefined`) — the one the audience
 * already has. PATCH replaces the whole definition, so a plain filter save must re-send the
 * stored prompt or lose it.
 */
export const promptForSave = (
  applied: string | null | undefined,
  stored: string | null | undefined,
): string | null => {
  // Not `??`: a cleared prompt is `null` and must stay null.
  if (applied !== undefined) return applied;
  return stored ?? null;
};

/**
 * Saved Target Lists → the Target Lists group's rows (NPJB-701 C2: editing prepopulates).
 * The upload's pre-clean count is not saved, so a restored row has none.
 */
export const targetListsToTalFiles = (targetLists: SavedTargetList[]): TalFile[] =>
  targetLists.map(({ uploadId, fileName, domainsCleaned }) => ({
    uid: uploadId,
    uploadId,
    fileName: fileName ?? uploadId,
    domainsCleaned,
    status: 'done' as const,
  }));

/**
 * Where a filter's canonical options are cached. Mirrors `map-brief-to-selections.ts`,
 * which resolves labels from a brief the same way — region and country come from the
 * geo endpoint, everything else from the generic `/audience/lookups` cache.
 */
const cacheKeyForFilter = (config: ExploreFilterConfig): readonly unknown[] => {
  if (config.key === 'geoRegion') return queryKeys.geo.regions(false);
  if (config.key === 'country') return queryKeys.geo.countries();
  return queryKeys.filterOptions.byType(config.apiType);
};

const ALL_CONFIGS = [...COMPANY_FILTER_CONFIGS, ...CONTACT_FILTER_CONFIGS];

/**
 * A stored definition → the rail's `FilterSelections` (NPJB-722 AC1, AC6).
 *
 * **Returns every key, including the empty ones.** That is what clears the groups the
 * audience does not constrain. Writing only the non-empty keys would leave whatever the
 * seller had checked before still checked, and AC1 says picking an audience clears every
 * option first.
 *
 * **A stored value the rail no longer offers is skipped** (AC6). Each stored value is
 * matched against the canonical options by `value`, not by label: a taxonomy rename
 * changes the label and keeps the value, and a rename must not silently drop a filter.
 * Unmatched values are left out of the rail and **left in the stored definition** —
 * nothing here writes back, so the row keeps them in case the option returns.
 *
 * Skipping rather than passing the raw value through is the same guard
 * `mapBriefToSelections` documents: an unknown value reaches the metrics query, matches
 * nothing, and silently returns an empty audience with no explanation (NPJB-392).
 *
 * The matched option wins over the stored copy, so a renamed label renders its new name.
 */
export const resolveDefinitionSelections = (
  definition: AudienceDefinition,
  queryClient: QueryClient,
): FilterSelections => {
  const out: FilterSelections = {};
  for (const key of DEFINITION_FILTER_KEYS) {
    out[key] = [];
  }
  for (const config of ALL_CONFIGS) {
    const stored = definition.filters[config.key as keyof SavedAudienceFilters] ?? [];
    if (stored.length === 0) continue;
    const options: FilterOption[] =
      industryHierarchyOptions(config.key, queryClient) ??
      queryClient.getQueryData<{ data: FilterOption[] }>(cacheKeyForFilter(config))?.data ??
      [];
    if (options.length === 0) {
      // The lookup has not loaded. Only `industry` and `country` are fetched on page
      // load; the rest fetch when their dropdown is first opened. An empty cache is
      // "I cannot tell yet", not "the taxonomy dropped every value" — so trust what was
      // stored. It was valid when it was saved, and the next open reconciles it.
      out[config.key] = stored;
      continue;
    }
    const wanted = new Set(stored.map((v) => v.value));
    out[config.key] = options
      .filter((opt) => wanted.has(opt.value))
      .map((opt) => ({
        id: opt.id,
        label: opt.label,
        value: opt.value,
        type: 'standard' as const,
      }));
  }
  return out;
};
